import { cn } from "@nexirt/ui";

/**
 * StatusPill — Rev 25.2 runbook Loop D. Render verbatim "● 1 slot · Jun"
 * mixed-case with a 7px green pulsing dot. Design HTML source
 * (docs/NEXIRT_DESIGN/Home.html line 525) reads "1 slot · Jun";
 * design CSS would uppercase via text-transform but the runbook
 * explicitly overrides — mixed-case is the desired final render so the
 * mono HUD feel comes from font + tracking, not capitalization.
 *
 * `variant="compact"` truncates to "1 slot" for the tablet band
 * (768-1099) where header real estate is tight.
 *
 * Decorative this phase (not interactive). Pair: header
 * (apps/web/src/components/Layout.tsx), mobile drawer
 * (apps/web/src/components/MobileDrawer.tsx), and any future footer
 * mount all render the same component to inherit design changes.
 */
export function StatusPill({
  label = "1 slot · Jun",
  compactLabel = "1 slot",
  variant = "full",
  className,
}: {
  label?: string;
  compactLabel?: string;
  variant?: "full" | "compact";
  className?: string;
}) {
  const shown = variant === "compact" ? compactLabel : label;
  return (
    <>
      <style>{STATUS_PILL_KEYFRAMES}</style>
      <span
        data-status-pill
        className={cn(
          "inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-nx-full border border-nx-line bg-nx-surface-2/70 px-3 py-1.5",
          "font-mono text-[11px] tracking-[0.12em] text-nx-text-faint",
          className,
        )}
        aria-label={`Availability: ${shown}`}
      >
        <span className="nx-status-pill-dot" aria-hidden />
        <span className="whitespace-nowrap">{shown}</span>
      </span>
    </>
  );
}

const STATUS_PILL_KEYFRAMES = `
@keyframes nx-status-pill-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
}
.nx-status-pill-dot {
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background: var(--nx-success);
  box-shadow: 0 0 12px var(--nx-success);
  animation: nx-status-pill-pulse 2.4s ease-in-out infinite;
  display: inline-block;
  flex-shrink: 0;
}
@media (prefers-reduced-motion: reduce) {
  .nx-status-pill-dot { animation: none !important; }
}
`;

export default StatusPill;
